import { ColumnDef, createColumnHelper } from '@tanstack/react-table';

import { Metrics } from 'components/insights/constants';
import { formatDaysValue, formatWholeNumber } from 'components/insights/formatting';
import AnalyticsResponseRollupCollection from 'components/insights/query/AnalyticsResponseRollupCollection';

import { buildEntityIdMap, buildEntityNameColumn, CELL_PADDING, CELL_PADDING_LEFT, renameNoneEntity } from './shared';

export type PatientVolumeRow = {
  entityName: string;
  entityId: string;
  volume: number;
  avgLos: number | null;
};

export function buildPatientVolumeRows(
  rollups: AnalyticsResponseRollupCollection,
  noneDisplayName?: string,
  groupingIndex = 1
): PatientVolumeRow[] {
  const entityGrouping = rollups.groupings[groupingIndex];
  if (!entityGrouping) return [];

  const idByName = buildEntityIdMap(rollups);
  const entityNames = entityGrouping.dimensions.getCurrentDimensionValuesByIndex(0);
  const volumes = entityGrouping.metrics.getCurrentSeries(Metrics.LOCATION_EPISODE_ID_COUNT);
  const avgLos = entityGrouping.metrics.getCurrentSeries(Metrics.LENGTH_OF_STAY_AVERAGE);

  return entityNames
    .map((name, i) => ({
      entityName: renameNoneEntity(name, noneDisplayName),
      entityId: idByName.get(name) ?? '',
      volume: volumes[i] ?? 0,
      avgLos: avgLos[i] ?? null,
    }))
    .filter((row) => row.entityName);
}

export const PATIENT_VOLUME_DEFAULT_SORT = { id: 'volume', desc: true };

export function buildPatientVolumeColumns(
  entityHeader: string,
  onEntityClick: (entityId: string, entityName: string) => void,
  groupTypeName: string
): ColumnDef<PatientVolumeRow, any>[] {
  const helper = createColumnHelper<PatientVolumeRow>();

  return [
    buildEntityNameColumn<PatientVolumeRow>(entityHeader, onEntityClick),
    helper.accessor('volume', {
      header: `${groupTypeName} Volume`,
      cell: (info) => formatWholeNumber(info.getValue()),
      size: 150,
      enableSorting: true,
      ...CELL_PADDING_LEFT,
    }),
    helper.accessor('avgLos', {
      header: 'Avg. Length of Service',
      cell: (info) => formatDaysValue(info.getValue()),
      size: 150,
      enableSorting: true,
      ...CELL_PADDING,
    }),
  ];
}
